import { Button } from "@frontend/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@frontend/components/ui/card"
import { Input } from "@frontend/components/ui/input"
import { Label } from "@frontend/components/ui/label"
import { useApiMutation, useApiQuery } from "@frontend/hooks/use-api"
import { zodResolver } from "@hookform/resolvers/zod"
import type { FormEvent } from "react"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"

interface UserProfile {
  id: string
  email: string
  name: string
  created_at: string
  updated_at?: string
}

const profileSchema = z.object({
  name: z.string().min(1, "Name is required").max(255)
})

type ProfileFormData = z.infer<typeof profileSchema>

export interface UserProfileViewProps {
  isLoading: boolean
  email: string
  nameError?: string
  saveError?: string
  saveSuccess: boolean
  isDirty: boolean
  isSaving: boolean
  register: ReturnType<typeof useForm<ProfileFormData>>["register"]
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

export function UserProfileView({
  isLoading,
  email,
  nameError,
  saveError,
  saveSuccess,
  isDirty,
  isSaving,
  register,
  onSubmit
}: UserProfileViewProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-muted-foreground text-center">Loading profile...</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>Manage your account information.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="profile-name">Name</Label>
            <Input id="profile-name" {...register("name")} aria-invalid={Boolean(nameError)} />
            {nameError ? <p className="text-xs text-destructive">{nameError}</p> : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-email">Email</Label>
            <Input id="profile-email" value={email} disabled readOnly />
            <p className="text-xs text-muted-foreground">
              Email is managed by your authentication provider and cannot be changed here.
            </p>
          </div>

          {saveError ? <p className="text-xs text-destructive">{saveError}</p> : null}
          {saveSuccess ? <p className="text-xs text-green-600">Profile saved.</p> : null}

          <div className="flex justify-end">
            <Button type="submit" disabled={!isDirty || isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export function UserProfileContainer() {
  const { data: user, isLoading } = useApiQuery<UserProfile>(["currentUser"], "/auth/me")

  const updateProfile = useApiMutation<UserProfile, ProfileFormData>("/users/me", {
    method: "PATCH",
    invalidateKeys: [["currentUser"]]
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema)
  })

  useEffect(() => {
    if (user) {
      reset({ name: user.name })
    }
  }, [user, reset])

  const onSubmit = (data: ProfileFormData) => {
    updateProfile.mutate(data)
  }

  return (
    <UserProfileView
      isLoading={isLoading}
      email={user?.email ?? ""}
      nameError={errors.name?.message}
      saveError={updateProfile.isError ? updateProfile.error.message : undefined}
      saveSuccess={updateProfile.isSuccess}
      isDirty={isDirty}
      isSaving={updateProfile.isPending}
      register={register}
      onSubmit={handleSubmit(onSubmit)}
    />
  )
}

export const UserProfile = UserProfileContainer
